import { useState } from "react";
import { Box } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { WelcomeOnboarding } from "./welcome-onboarding/welcome-onboarding";
import { OverviewOnboarding } from "./overview-onboarding/overview-onboarding";
import { InteractionOnboarding } from "./interaction-onboarding/interaction-onboarding";
import { GenerateOnbording } from "./generate-onbording/generate-onbording";
import { StatisticOnboarding } from "./statistic-onboarding/statistic-onboarding";
import { MenuOnboarding } from "./menu-onboarding/menu-onboarding";
import { ChooseOnboarding } from "./choose-onboarding/choose-onboarding";
import { InfoOnboarding } from "./info-onboarding/info-onboarding";
import { ContextMenuOnboarding } from "./context-menu-onboarding/context-menu-onboarding";

export const OnboardingScreen = () => {
  const [screen, setScreen] = useState<
    | "welcome"
    | "overview"
    | "interaction"
    | "generate"
    | "statistic"
    | "menu"
    | "choose"
    | "info"
    | "context"
  >("welcome");

  const handleGetStarted = () => {
    setScreen("overview");
  };

  const handlePrevPage = () => {
    switch (screen) {
      case "overview":
        setScreen("welcome");
        break;
      case "interaction":
        setScreen("overview");
        break;
      case "generate":
        setScreen("interaction");
        break;
      case "statistic":
        setScreen("generate");
        break;
      case "info":
        setScreen("statistic");
        break;
      case "menu":
        setScreen("info");
        break;
      case "choose":
        setScreen("menu");
        break;
      default:
        break;
    }
  };

  const handleNextPage = () => {
    switch (screen) {
      case "welcome":
        setScreen("overview");
        break;
      case "overview":
        setScreen("interaction");
        break;
      case "interaction":
        setScreen("generate");
        break;
      case "generate":
        setScreen("statistic");
        break;
      case "statistic":
        setScreen("info");
        break;
      case "info":
        setScreen("context");
        break;
      case "context":
        setScreen("menu");
        break;
      case "menu":
        setScreen("choose");
        break;
      default:
        break;
    }
  };

  const handleSkip = () => {
    setScreen("choose");
  };

  const variants = {
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -50 },
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100dvh",
      }}
    >
      <AnimatePresence mode="wait">
        {screen === "welcome" && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{
              opacity: 0,
              y: 0,
              transition: { delay: 0.5, duration: 0.5 },
            }}
            transition={{ duration: 0.5 }}
            style={{ width: "100%", height: "100%", overflowY: "auto" }}
          >
            <WelcomeOnboarding
              onGetStarted={handleGetStarted}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "overview" && (
          <motion.div
            key="overview"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <OverviewOnboarding
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "interaction" && (
          <motion.div
            key="interaction"
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
            }}
          >
            <InteractionOnboarding
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "generate" && (
          <motion.div
            key="generate"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
            }}
          >
            <GenerateOnbording
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "info" && (
          <motion.div
            key="info"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "scroll",
            }}
          >
            <InfoOnboarding
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "statistic" && (
          <motion.div
            key="statistic"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
            }}
          >
            <StatisticOnboarding
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "context" && (
          <motion.div
            key="context"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <ContextMenuOnboarding handleNextPage={handleNextPage} />
          </motion.div>
        )}

        {screen === "menu" && (
          <motion.div
            key="menuOnboarding"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
            }}
          >
            <MenuOnboarding
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              handleSkip={handleSkip}
            />
          </motion.div>
        )}

        {screen === "choose" && (
          <motion.div
            key="chooseOnboarding"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.5 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 10,
              overflowY: "auto",
            }}
          >
            <ChooseOnboarding />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};
